import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import api from '../../services/api';
import HorsePickerInline from './HorsePickerInline';

export default function ApplyProtocolModal({ open, onClose, onSaved, protocol, availableHorses, preselectedHorse }) {
  const today = new Date().toLocaleDateString('sv');
  const isSingleHorse = !!preselectedHorse;

  const [selectedHorseIds, setSelectedHorseIds] = useState([]);
  const [dateMode, setDateMode]                 = useState('same');
  const [globalDate, setGlobalDate]             = useState(today);
  const [perHorseDates, setPerHorseDates]       = useState({});
  const [applying, setApplying]                 = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedHorseIds(isSingleHorse ? [preselectedHorse.id] : []);
    setDateMode('same');
    setGlobalDate(today);
    setPerHorseDates({});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Sync globalDate into perHorseDates when it changes
  useEffect(() => {
    if (dateMode === 'per_horse' && selectedHorseIds.length > 0) {
      setPerHorseDates(prev => {
        const next = { ...prev };
        selectedHorseIds.forEach(id => { if (!next[id]) next[id] = globalDate; });
        return next;
      });
    }
  }, [dateMode, selectedHorseIds, globalDate]);

  const handleApply = async () => {
    if (!selectedHorseIds.length) { toast.error('Select at least one horse'); return; }
    setApplying(true);
    try {
      let body;
      if (dateMode === 'same') {
        body = { horse_ids: selectedHorseIds, start_date: globalDate };
      } else {
        body = {
          horse_assignments: selectedHorseIds.map(hid => ({
            horse_id:   hid,
            start_date: perHorseDates[hid] || globalDate,
          })),
        };
      }
      const res = await api.post(`/farmfolio/protocols/${protocol.id}/apply`, body);
      toast.success(`Protocol applied — ${res.data.created} schedule item(s) created`);
      onSaved();
      onClose();
    } catch { toast.error('Failed to apply protocol'); }
    setApplying(false);
  };

  const itemCount = (protocol?.schedule_items || []).length;
  const horses    = availableHorses || [];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Apply Protocol — {protocol?.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {protocol?.reason && (
            <p className="text-xs text-muted-foreground italic">{protocol.reason}</p>
          )}
          <p className="text-xs text-muted-foreground">
            This will create <span className="font-medium">{itemCount} scheduled care item(s)</span> {isSingleHorse ? 'for this horse' : 'per selected horse'},
            with dates calculated relative to the start date.
          </p>

          {isSingleHorse ? (
            <div className="space-y-1">
              <Label className="font-semibold">Horse</Label>
              <div className="rounded border px-3 py-2 text-sm bg-muted/20 font-medium">
                {preselectedHorse.system_name || preselectedHorse.name}
              </div>
            </div>
          ) : (
            <div className="space-y-1">
              <Label className="font-semibold">Horses</Label>
              <HorsePickerInline
                availableHorses={horses}
                value={selectedHorseIds}
                onChange={setSelectedHorseIds}
                mode="multi"
              />
            </div>
          )}

          {/* Date mode toggle — hide per-horse option when single horse */}
          <div className="space-y-2">
            <Label className="font-semibold">Start Date</Label>
            {!isSingleHorse && (
              <div className="flex gap-1">
                {[
                  { value: 'same',      label: 'Same date for all' },
                  { value: 'per_horse', label: 'Per horse' },
                ].map(opt => (
                  <button key={opt.value} type="button"
                    onClick={() => setDateMode(opt.value)}
                    className={`flex-1 py-1.5 px-2 rounded text-xs font-medium border transition-colors ${
                      dateMode === opt.value
                        ? 'bg-primary text-white border-primary'
                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                    }`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            )}

            {dateMode === 'same' ? (
              <Input type="date" value={globalDate} onChange={e => setGlobalDate(e.target.value)}
                className="time-filled" />
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground shrink-0">Default date:</Label>
                  <Input type="date" value={globalDate}
                    onChange={e => {
                      setGlobalDate(e.target.value);
                      setPerHorseDates(prev => {
                        const next = { ...prev };
                        selectedHorseIds.forEach(id => { next[id] = e.target.value; });
                        return next;
                      });
                    }}
                    className="h-7 text-xs time-filled flex-1" />
                </div>
                {selectedHorseIds.length > 0 && (
                  <div className="border rounded divide-y max-h-48 overflow-y-auto">
                    {selectedHorseIds.map(hid => {
                      const horse = horses.find(h => h.id === hid);
                      return (
                        <div key={hid} className="flex items-center gap-2 px-2 py-1.5">
                          <span className="text-xs flex-1 truncate font-medium">
                            {horse?.system_name || horse?.name || hid}
                          </span>
                          <Input type="date"
                            value={perHorseDates[hid] || globalDate}
                            onChange={e => setPerHorseDates(prev => ({ ...prev, [hid]: e.target.value }))}
                            className="h-6 text-[10px] w-32 time-filled" />
                        </div>
                      );
                    })}
                  </div>
                )}
                {selectedHorseIds.length === 0 && (
                  <p className="text-xs text-muted-foreground italic text-center py-2">Select horses above to set individual dates.</p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={onClose} disabled={applying}>Cancel</Button>
          <Button onClick={handleApply} disabled={applying || !selectedHorseIds.length}
            className="bg-primary hover:bg-primary/90 text-white">
            {applying ? 'Applying…' : isSingleHorse ? 'Apply Protocol' : `Apply to ${selectedHorseIds.length || 0} horse(s)`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
